import clsx from 'clsx';
import type { FunctionComponent } from 'react';

import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Badge } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import { IconChevronRightSmall } from '~/ui/core/icons';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';

import { potentialBandTranslationKey } from '../../ai-transformation/model/automation';
import { BAND_BADGE, formatHours } from './PositionAutomationBrief';
import type { PositionAutomation, PositionAutomationTask } from './position-automation';

const TASK_GRID = 'grid grid-cols-[2rem_minmax(0,1fr)_6.5rem_6.5rem_1rem] items-start gap-m';
const TASK_GRID_WITHOUT_HOURS = 'grid grid-cols-[2rem_minmax(0,1fr)_6.5rem_1rem] items-start gap-m';

const LEVER_LABEL: Record<string, TranslationKey> = {
    AGENTIC_AI: 'pages.position-management.detail.automation.levers.agentic-ai',
    OTHER_TOOL: 'pages.position-management.detail.automation.levers.other-tool',
    PHYSICAL_AUTOMATION: 'pages.position-management.detail.automation.levers.physical-automation',
    SOFTWARE_RPA: 'pages.position-management.detail.automation.levers.software-rpa',
};

const TaskRow: FunctionComponent<{
    readonly index: number;
    readonly isSelected: boolean;
    readonly onOpen: (() => void) | undefined;
    readonly showHours: boolean;
    readonly task: PositionAutomationTask;
    readonly untitled: string;
}> = ({ index, isSelected, onOpen, showHours, task, untitled }) => {
    const translate = useTranslate();
    const {
        applicationState: { language },
    } = useApplicationContext();
    const locale = language.code;
    const levers = new Intl.ListFormat(locale, { style: 'short', type: 'unit' }).format(
        task.levers.flatMap((lever) => {
            const key = LEVER_LABEL[lever];

            return key === undefined ? [] : [translate(key)];
        }),
    );
    const grid = showHours ? TASK_GRID : TASK_GRID_WITHOUT_HOURS;
    const content = (
        <>
            <span className="text-body-xs tabular-nums text-text-muted">{String(index + 1).padStart(2, '0')}</span>
            <span className="flex min-w-0 flex-col">
                <span className="text-body-s text-text-primary">{task.title ?? untitled}</span>
                {levers === '' ? null : <span className="text-body-xs text-text-muted">{levers}</span>}
            </span>
            {showHours ? (
                <span className="flex flex-col items-end text-right tabular-nums">
                    {task.automatableHoursPerWeek === undefined ? (
                        <span className="text-body-xs text-text-muted">–</span>
                    ) : (
                        <span className="text-body-s text-text-primary">
                            {translate('pages.position-management.detail.automation.task-hours', {
                                hours: formatHours(task.automatableHoursPerWeek, locale),
                            })}
                        </span>
                    )}
                    {task.hoursPerWeek === undefined ? null : (
                        <span className="text-body-xs text-text-muted">
                            {translate('pages.position-management.detail.automation.task-total-hours', {
                                hours: formatHours(task.hoursPerWeek, locale),
                            })}
                        </span>
                    )}
                </span>
            ) : null}
            <span className="flex justify-end">
                {task.isAssessmentKnown && task.band === undefined ? (
                    <span className="text-body-xs text-text-muted">
                        {translate(potentialBandTranslationKey(undefined))}
                    </span>
                ) : null}
                {task.band === undefined ? null : (
                    <Badge variant={BAND_BADGE[task.band]}>{translate(potentialBandTranslationKey(task.band))}</Badge>
                )}
            </span>
            {onOpen === undefined ? <span /> : <IconChevronRightSmall className="text-text-muted" size={16} />}
        </>
    );

    return (
        <li className="list-none border-t border-border-subtle first:border-t-0" data-testid="position-automation-task">
            {onOpen === undefined ? (
                <div className={clsx(grid, 'px-xs py-m')}>{content}</div>
            ) : (
                <Button
                    variant="plain"
                    aria-pressed={isSelected}
                    className={clsx(
                        grid,
                        'w-full cursor-pointer rounded-md border-0 bg-transparent px-xs py-m text-left font-[inherit] hover:bg-surface-hover focus-visible:outline-1 focus-visible:outline-focus-ring',
                        isSelected && 'bg-surface-hover',
                    )}
                    type="button"
                    onClick={onOpen}
                >
                    {content}
                </Button>
            )}
        </li>
    );
};

export const PositionAutomationTasks: FunctionComponent<{
    /** Undefined while the tasks' assessments load. */
    readonly automation: PositionAutomation | undefined;
    readonly onOpenTask: ((task: { readonly id: string; readonly version: number }) => void) | undefined;
    readonly openTaskId: string | undefined;
    /** False where the tasks' weekly hours are not known, such as for a role's tasks; leaves the hours column out. */
    readonly showHours?: boolean;
}> = ({ automation, onOpenTask, openTaskId, showHours = true }) => {
    const translate = useTranslate();
    const untitled = translate('pages.position-management.detail.profile.untitled');

    if (automation === undefined) {
        return (
            <div className="flex flex-col gap-s">
                <Skeleton height="2.5rem" width="100%" />
                <Skeleton height="2.5rem" width="100%" />
                <Skeleton height="2.5rem" width="100%" />
            </div>
        );
    }

    if (automation.taskCount === 0) {
        return (
            <p className="m-0 text-body-s text-text-muted">{translate('pages.position-alignment.tasks-step.empty')}</p>
        );
    }

    return (
        <div className="flex flex-col gap-xs" data-testid="position-automation-tasks">
            <div
                className={clsx(showHours ? TASK_GRID : TASK_GRID_WITHOUT_HOURS, 'px-xs text-body-xs text-text-muted')}
            >
                <span />
                <span>{translate('pages.position-management.detail.automation.task')}</span>
                {showHours ? (
                    <span className="text-right">
                        {translate('pages.position-management.detail.automation.hours-column')}
                    </span>
                ) : null}
                <span className="text-right">{translate('pages.position-management.detail.automation.potential')}</span>
                <span />
            </div>
            <ul className="m-0 flex flex-col p-0">
                {automation.tasks.map((task, index) => (
                    <TaskRow
                        key={task.id}
                        index={index}
                        isSelected={task.id === openTaskId}
                        showHours={showHours}
                        task={task}
                        untitled={untitled}
                        onOpen={
                            onOpenTask === undefined || task.version <= 0
                                ? undefined
                                : () => onOpenTask({ id: task.id, version: task.version })
                        }
                    />
                ))}
            </ul>
        </div>
    );
};
